/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * The one comma-separated list input in the console — `NumberField`'s sibling, and it exists for
 * the same reason (`#1199`).
 *
 * A control whose **text** and whose **value** are different shapes must own the text. Parsing on
 * every keystroke and rendering back from the parse deletes every intermediate state the parser
 * cannot represent: for a number that is `''`, and for a list it is **the separator itself**. That
 * is what shipped in `XS1`'s `is one of` control — typing `kids,` parsed to `['kids']` and
 * re-rendered as `kids`, so a second member could never be started.
 *
 * The parent still owns the value; the draft re-syncs only when the parent's list disagrees with
 * what the draft already means, or `kids, ` would snap back to `kids` on the keystroke after the
 * comma.
 *
 * **A field using this must accept an empty list** and require one in its own validation, for the
 * reason `NumberField` must accept `undefined`: coercing inside the control puts the defect back
 * one layer down.
 */

import React from 'react';
import { Input, type InputProps } from '@chakra-ui/react';
import { listToDraft, parseListDraft, shouldResyncListDraft } from './ListField.helpers';

export interface ListFieldProps extends Omit<InputProps, 'value' | 'onChange'> {
  /** The current list. `undefined` or `[]` renders an empty box. */
  value: readonly (string | number | boolean)[] | undefined;
  /** Called with the parsed members. Empty text is `[]`, never `undefined`: the operator cleared it. */
  onChange: (value: string[]) => void;
}

export const ListField: React.FC<ListFieldProps> = ({ value, onChange, ...rest }) => {
  const [draft, setDraft] = React.useState<string>(() => listToDraft(value));

  React.useEffect(() => {
    setDraft((current) => (shouldResyncListDraft(current, value) ? listToDraft(value) : current));
  }, [value]);

  return (
    <Input
      {...rest}
      value={draft}
      onChange={(e) => {
        const next = e.target.value;
        setDraft(next);
        onChange(parseListDraft(next));
      }}
    />
  );
};
